import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Modal, Tag, Tooltip } from 'antd';
import { CheckOutlined, DownloadOutlined, MessageOutlined, UploadOutlined } from '@ant-design/icons';
import DataTable from '@/components/data-table';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import { ClearAllFiltersButton } from '@/components/data-table-actions';
import { API } from '@/services/api.service';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { date, money, saveBlob } from '@/app/suppliers/_components/format';
import useColumnHeaders from '@/app/sef-state/_components/useColumnHeaders';
import { columnKeysOf, listQueryOf, noop } from '@/app/sef-state/_components/helpers';
import ImportModal from './ImportModal';
import RecipientSidebar from './RecipientSidebar';
import { ADDRESS_STATE_COLORS, REPLY_COLORS, STATUS_COLORS, addressStateOf, errorText } from './helpers';

const FILTERS = ['name', 'pib', 'emails', 'addressState', 'warnings', 'status', 'reply'];
const SORTS = ['name', 'pib', 'balance', 'status', 'lastInvoiceAt', 'sentAt'];
const KEYS = columnKeysOf(FILTERS, SORTS);
const DEFAULTS = Object.fromEntries(KEYS.map((k) => [k, undefined]));
const LABEL_KEYS = ['key', 'pib', 'codes', 'name', 'emails', 'source', 'reply', 'status', 'balance', 'sentAt', 'deadline'];

/** Everyone the request goes to: where their address stands and what happened to the mail. */
export default function RecipientsTable({ companyId, request, canWrite, onChanged }) {
  const { t } = useTranslation(['partnerRequests', 'tables']);
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [exporting, setExporting] = useState(false);
  const requestId = request._id;
  const query = listQueryOf(queryParams);

  const list = useQuery({
    queryKey: ['partnerRequests', requestId, 'recipients', query],
    queryFn: () => API.partnerRequests.getRecipients(companyId, requestId, query),
    placeholderData: keepPreviousData,
    refetchInterval: request.status === 'sending' ? 30000 : false,
  });
  const { data } = list;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['partnerRequests', requestId] });
    onChanged();
  };

  const confirm = useMutation({
    mutationFn: () => API.partnerRequests.confirmRecipients(companyId, requestId, { filter: query }),
    onSuccess: (result) => {
      setConfirming(false);
      message.success(t('table.confirmed', { count: result.confirmed, skipped: result.skippedBad }));
      refresh();
    },
  });
  const reply = useMutation({
    mutationFn: (value) => API.partnerRequests.markReply(companyId, requestId, { ids: selected, reply: value }),
    onSuccess: () => {
      setSelected([]);
      refresh();
    },
    onError: (error) => message.error(errorText(t, error)),
  });

  const download = async (filters) => {
    setExporting(true);
    try {
      const labels = LABEL_KEYS.map((k) => t(`export.${k}`));
      const { blob, fileName } = await API.partnerRequests.exportRecipients(companyId, requestId, filters, labels);
      saveBlob(blob, fileName);
    } catch (error) {
      message.error(errorText(t, error));
    } finally {
      setExporting(false);
    }
  };

  const h = useColumnHeaders(queryParams, setQueryParams, FILTERS);
  const c = (key) => t(`columns.${key}`);
  const optionsOf = (group, values) => values.map((value) => ({ value, label: t(`${group}.${value}`) }));

  const columns = useMemo(
    () => [
      {
        title: h.text('name', c('name')),
        dataIndex: 'name',
        width: 200,
        render: (v) => <TruncateTooltipCell label={v} />,
      },
      {
        title: h.text('pib', c('pib')),
        dataIndex: 'pib',
        width: 95,
        render: (v, r) => v || <Tooltip title={r.codes.join(', ')}>{t('noPib')}</Tooltip>,
      },
      {
        title: h.text('emails', c('emails')),
        dataIndex: 'emails',
        width: 190,
        render: (v) => <TruncateTooltipCell label={v.join('; ')} />,
      },
      {
        title: h.options('addressState', c('addressState'), optionsOf('addressStates', ['missing', 'proposed', 'confirmed', 'excluded'])),
        key: 'addressState',
        width: 100,
        render: (r) => {
          const state = addressStateOf(r);
          return <Tag color={ADDRESS_STATE_COLORS[state]}>{t(`addressStates.${state}`)}</Tag>;
        },
      },
      {
        title: h.options('warnings', c('warnings'), optionsOf('warnings', ['shared', 'free', 'multiple'])),
        dataIndex: 'warnings',
        width: 115,
        render: (v) =>
          v.map((w) => (
            <Tooltip key={w} title={t(`warningHelp.${w}`)}>
              <Tag color="gold">{t(`warnings.${w}`)}</Tag>
            </Tooltip>
          )),
      },
      {
        title: h.sort('balance', c('balance')),
        dataIndex: 'balance',
        width: 100,
        align: 'right',
        render: money,
      },
      {
        title: h.options('status', c('status'), optionsOf('statuses', Object.keys(STATUS_COLORS))),
        dataIndex: 'status',
        width: 95,
        render: (v) => <Tag color={STATUS_COLORS[v]}>{t(`statuses.${v}`)}</Tag>,
      },
      { title: h.sort('sentAt', c('sentAt')), dataIndex: 'sentAt', width: 90, render: date },
      {
        title: h.options('reply', c('reply'), optionsOf('replies', ['none', 'replied', 'repliedWithAttachment'])),
        dataIndex: 'reply',
        width: 115,
        render: (v, r) => (
          <>
            {v && <Tag color={REPLY_COLORS[v]}>{t(`replies.${v}`)}</Tag>}
            {!v && r.autoReplyAt && <Tag>{t('replies.auto')}</Tag>}
            {r.reminder && <Tag color="purple">{t(`reminders.${r.reminder}`)}</Tag>}
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParams, t]
  );

  const open = (data?.items || []).find((r) => r._id === openId) || null;

  return (
    <>
      {confirm.isError && <Alert className="mb-2" type="error" showIcon message={errorText(t, confirm.error)} />}
      <DataTable
        rowKey="_id"
        title={`${t('table.title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={list.isLoading ? 'pending' : list.error ? 'error' : 'success'}
        columns={columns}
        offset={queryParams.offset}
        limit={queryParams.limit}
        onChangePagination={noop}
        onRow={(r) => ({ onClick: () => setOpenId(r._id), className: 'cursor-pointer' })}
        rowSelection={
          canWrite
            ? {
                selectedRowKeys: selected,
                onChange: setSelected,
                columnWidth: 40,
                onCell: () => ({ onClick: (e) => e.stopPropagation() }),
              }
            : undefined
        }
        tableLayout="fixed"
        scroll={{ x: columns.reduce((s, col) => s + (col.width || 0), 40) }}
        size="small"
        headerChildren={
          <>
            <ClearAllFiltersButton
              label={t('clearAllFilters', { ns: 'tables' })}
              onReset={() => setQueryParams({ ...DEFAULTS, offset: 1 })}
              defaultParams={DEFAULTS}
            />
            {canWrite && selected.length > 0 && (
              <>
                <Button
                  type="default"
                  shape="round"
                  className="btn-default h-8 text-black"
                  icon={<MessageOutlined className="text-zinc-400" />}
                  loading={reply.isPending}
                  onClick={() => reply.mutate('replied')}
                >
                  {t('table.markReplied', { count: selected.length })}
                </Button>
                <Button
                  type="default"
                  shape="round"
                  className="btn-default h-8 text-black"
                  loading={reply.isPending}
                  onClick={() => reply.mutate('repliedWithAttachment')}
                >
                  {t('table.markWithAttachment')}
                </Button>
              </>
            )}
            {canWrite && (
              <Button
                type="default"
                shape="round"
                className="btn-default h-8 text-black"
                icon={<CheckOutlined className="text-zinc-400" />}
                onClick={() => setConfirming(true)}
              >
                {t('table.confirmFiltered')}
              </Button>
            )}
            {canWrite && (
              <Button
                type="default"
                shape="round"
                className="btn-default h-8 text-black"
                icon={<UploadOutlined className="text-zinc-400" />}
                onClick={() => setImporting(true)}
              >
                {t('table.import')}
              </Button>
            )}
            <Button
              type="default"
              shape="round"
              className="btn-default h-8 text-black"
              icon={<DownloadOutlined className="text-zinc-400" />}
              loading={exporting}
              disabled={!data?.count}
              onClick={() => download(query)}
            >
              {t('table.excel')}
            </Button>
          </>
        }
      />
      <RecipientSidebar
        companyId={companyId}
        requestId={requestId}
        recipient={open}
        canWrite={canWrite}
        onClose={() => setOpenId(null)}
        onChanged={refresh}
      />
      <ImportModal
        open={importing}
        companyId={companyId}
        requestId={requestId}
        templateBusy={exporting}
        onTemplate={() => download({})}
        onClose={() => setImporting(false)}
        onDone={(result) => {
          message.success(t('import.done', { addresses: result.addresses, replies: result.replies, skipped: result.skipped.length }));
          refresh();
        }}
      />
      <Modal
        open={confirming}
        title={t('table.confirmTitle')}
        onCancel={() => setConfirming(false)}
        footer={[
          <Button key="c" shape="round" onClick={() => setConfirming(false)}>
            {t('cancel')}
          </Button>,
          <Button key="o" shape="round" type="primary" loading={confirm.isPending} onClick={() => confirm.mutate()}>
            {t('table.confirmSubmit')}
          </Button>,
        ]}
      >
        <p>{t('table.confirmText', { count: data?.count || 0 })}</p>
      </Modal>
    </>
  );
}
